import React, { useMemo, useState } from "react";
import useAIConfigs from "@/hooks/useAIConfigs";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import IconEditPencil from "@/icons/IconEditPencil";
import IconTrash2 from "@/icons/IconTrash2";

type SortField = "name" | "model" | "language" | "status";
type SortDir = "asc" | "desc";

const STATUS_STYLES: Record<string, string> = {
  active:   "bg-green-100 text-green-800",
  inactive: "bg-slate-100 text-slate-800",
  crawling: "bg-blue-100 text-blue-800",
  error:    "bg-red-100 text-red-800"
};

const STATUS_TIPS: Record<string, string> = {
  active:   "Ready — has a knowledge index and is deployed",
  inactive: "Not yet deployed to any channel",
  crawling: "Knowledge index is being built",
  error:    "Index error — check training settings"
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      title={STATUS_TIPS[status] ?? status}
      className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-default ${STATUS_STYLES[status] ?? STATUS_STYLES.inactive}`}
    >
      {status === "active" ? "Ready" : status}
    </span>
  );
}

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (field !== sortField) return <span className="ml-1 text-slate-300">↕</span>;
  return <span className="ml-1 text-slate-600">{sortDir === "asc" ? "↑" : "↓"}</span>;
}

export default function AINewSettings() {
  const { aiConfigurations, loading, remove } = useAIConfigs();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const handleEdit = (id: number) => navigate(`/ai-new/settings/general/${id}`);

  const handleDelete = (config: any, index: number) => {
    if (!confirm(`Are you sure you want to delete "${config.name}"?`)) return;
    remove(config, index);
  };

  const toggleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = aiConfigurations.filter(c => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (!q) return true;
      return (
        c.name?.toLowerCase().includes(q) ||
        c.aiModel?.toLowerCase().includes(q) ||
        c.language?.toLowerCase().includes(q) ||
        c.status?.toLowerCase().includes(q)
      );
    });

    list.sort((a, b) => {
      const av = ((a as any)[sortField === "model" ? "aiModel" : sortField] ?? "").toString().toLowerCase();
      const bv = ((b as any)[sortField === "model" ? "aiModel" : sortField] ?? "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });

    return list;
  }, [aiConfigurations, search, sortField, sortDir]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (aiConfigurations.length === 0) {
    return (
      <div className="p-4">
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <div className="text-slate-400 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <p className="text-slate-500 mb-1">No chatbot configurations yet</p>
          <p className="text-sm text-slate-400">Click "New Chatbot" to create your first AI chatbot configuration</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filter / toolbar bar */}
      <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, model, or language…"
            className="text-xs pl-7 pr-3 py-1.5 rounded-md border border-slate-200 bg-white w-64 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]/40"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]/40"
        >
          <option value="all">All statuses</option>
          <option value="active">Ready</option>
          <option value="inactive">Inactive</option>
          <option value="crawling">Building index</option>
          <option value="error">Error</option>
        </select>
        {search && (
          <span className="text-xs text-slate-400 tabular-nums ml-auto">
            {filtered.length} of {aiConfigurations.length}
          </span>
        )}
      </div>

      <div className="bg-white overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              {(["name", "model", "language", "status"] as SortField[]).map(col => (
                <th
                  key={col}
                  onClick={() => toggleSort(col)}
                  className="text-left px-6 py-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-600 whitespace-nowrap"
                >
                  {col.charAt(0).toUpperCase() + col.slice(1)}
                  <SortIcon field={col} sortField={sortField} sortDir={sortDir} />
                </th>
              ))}
              <th className="px-6 py-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-sm text-slate-400">
                  No results for "{search}"
                </td>
              </tr>
            ) : filtered.map((config, index) => (
              <tr key={config.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                <td className="px-6 py-3">
                  <button
                    onClick={() => handleEdit(config.id)}
                    className="font-medium text-sm text-[var(--color-primary)] hover:underline text-left"
                  >
                    {config.name}
                  </button>
                </td>
                <td className="px-6 py-3 text-sm text-slate-600">{config.aiModel}</td>
                <td className="px-6 py-3 text-sm text-slate-600">{config.language}</td>
                <td className="px-6 py-3"><StatusBadge status={config.status} /></td>
                <td className="px-6 py-3">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => handleEdit(config.id)}
                      title="Edit"
                      className="p-1.5 rounded text-slate-400 hover:text-[var(--color-primary)] hover:bg-slate-100 transition-colors"
                    >
                      <IconEditPencil style={{ width: 16, height: 16 }} />
                    </button>
                    <button
                      onClick={() => handleDelete(config, index)}
                      title="Delete"
                      className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <IconTrash2 style={{ width: 16, height: 16 }} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {false && (
        <p className="mt-2 text-xs text-slate-400">
          {filtered.length} of {aiConfigurations.length} configuration{aiConfigurations.length !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}
